"use client"
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
    ENGLISH_BETA_LEGAL_SUMMARY_URL,
    ENGLISH_BETA_PRIVACY_URL,
    ENGLISH_BETA_SCTA_URL,
    ENGLISH_BETA_TERMS_URL,
    isEnglishOperatorMode,
    rememberOperatorSurfaceFromLocation,
    withOperatorSurface,
} from "../utils/operatorMode";

const API_URL = window.location.hostname === "localhost"
    ? (process.env.REACT_APP_API_URL || "http://localhost:3173")
    : window.location.origin;

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

const EMAIL_DOMAIN_SUGGESTIONS = {
    'gmal.com': 'gmail.com',
    'gmial.com': 'gmail.com',
    'gnail.com': 'gmail.com',
    'gmail.co': 'gmail.com',
    'gmail.con': 'gmail.com',
    'hotmial.com': 'hotmail.com',
    'hotmai.com': 'hotmail.com',
    'outlok.com': 'outlook.com',
    'outlook.con': 'outlook.com',
    'protonmial.com': 'protonmail.com',
    'protonmai.com': 'protonmail.com',
    'yaho.com': 'yahoo.com',
    'yahoo.con': 'yahoo.com',
};

const getEmailSuggestion = (value) => {
    const normalized = normalizeEmail(value);
    const atIndex = normalized.lastIndexOf('@');
    if (atIndex <= 0) return null;
    const local = normalized.slice(0, atIndex);
    const domain = normalized.slice(atIndex + 1);
    const suggestedDomain = EMAIL_DOMAIN_SUGGESTIONS[domain];
    return suggestedDomain ? `${local}@${suggestedDomain}` : null;
};

const SignupPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const isEnglishBeta = isEnglishOperatorMode();

    // --- フォームの状態管理 ---
    const [email, setEmail] = useState('');
    const [confirmEmail, setConfirmEmail] = useState('');
    const [password, setPassword] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [honeypot, setHoneypot] = useState(''); // スパム対策用
    const [honeypotRetryAllowed, setHoneypotRetryAllowed] = useState(false);
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [showInviteCodeField, setShowInviteCodeField] = useState(false);

    // 招待コード（インボイスコード）の取得
    useEffect(() => {
        rememberOperatorSurfaceFromLocation(location.search);
        const params = new URLSearchParams(window.location.search);
        const codeFromUrl = params.get('code');
        const emailFromUrl = params.get('email');
        if (codeFromUrl) {
            setInviteCode(codeFromUrl);
            setShowInviteCodeField(true);
        }
        if (isEnglishBeta && emailFromUrl && !email) {
            setEmail(emailFromUrl);
        }
        if (isEnglishBeta) {
            document.title = "SalesBreaker | English Operator Signup";
        }
    }, [email, isEnglishBeta, location.search]);

    // --- Helpers ---
    const getCookie = (name) => {
        try {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop().split(';').shift();
        } catch (e) {
            return null;
        }
        return null;
    };

    // --- ハンドラー ---

    // Googleで登録開始
    const handleGoogleSignup = () => {
        const fbp = getCookie('_fbp');
        const fbc = getCookie('_fbc');
        // オーガニック登録
        const englishBetaParams = isEnglishBeta ? '&surface=english_beta&operator_surface=english_beta' : '';
        const signupSource = isEnglishBeta ? 'english_beta_google' : 'organic_web';
        window.location.href = `${API_URL}/api/auth/google?signup_source=${signupSource}&fbp=${fbp || ''}&fbc=${fbc || ''}${englishBetaParams}`;
    };

    // 通常のメールアドレス登録
    const handleSubmit = async (event) => {
        event.preventDefault();

        // Contact/password autofill can populate hidden text inputs. Keep the first
        // trip as a one-shot bot signal, but give a real user one explicit retry.
        if (honeypot) {
            setHoneypot('');
            if (!honeypotRetryAllowed) {
                setHoneypotRetryAllowed(true);
                setError(isEnglishBeta
                    ? 'Browser autofill also completed a hidden anti-spam field. Please review the visible details and submit once more.'
                    : 'ブラウザの自動入力が非表示のスパム対策欄にも入りました。表示中の内容を確認し、もう一度「同意して登録する」を押してください。');
                return;
            }
        }

        const isJapanese = (str) => /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]/.test(str);
        if (!isEnglishBeta && (!isJapanese(companyName) || !isJapanese(name))) {
            setError("会社名とお名前には、日本語（漢字・ひらがな・カタカナ）を含めて入力してください。");
            return;
        }

        const normalizedEmail = normalizeEmail(email);
        const normalizedConfirmEmail = normalizeEmail(confirmEmail);
        if (isEnglishBeta && normalizedEmail !== normalizedConfirmEmail) {
            setError("The email addresses do not match. Please check the spelling before creating the account.");
            return;
        }

        setProcessing(true);
        setError(null);

        const fbp = getCookie('_fbp');
        const fbc = getCookie('_fbc');

        try {
            const response = await fetch(`${API_URL}/api/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: normalizedEmail || email,
                    confirm_email: isEnglishBeta ? normalizedConfirmEmail : undefined,
                    password,
                    company: companyName,
                    name,
                    phone,
                    inviteCode,
                    fbp: fbp || null,
                    fbc: fbc || null,
                    signup_source: isEnglishBeta ? 'english_beta_email' : 'organic_web',
                    surface: isEnglishBeta ? 'english_beta' : undefined,
                    operator_surface: isEnglishBeta ? 'english_beta' : undefined
                }),
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.error || (isEnglishBeta ? 'Signup failed.' : '登録に失敗しました。'));

            if (isEnglishBeta) {
                try {
                    window.sessionStorage.setItem('english_beta_signup_email', normalizedEmail || email);
                } catch (storageError) {
                    // Ignore storage failures in restricted browser contexts.
                }
            }

            navigate(withOperatorSurface('/signup-thanks'));
        } catch (err) {
            setError(err.message);
        } finally {
            setProcessing(false);
        }
    };

    const emailSuggestion = isEnglishBeta ? getEmailSuggestion(email) : null;
    const showConfirmEmailMismatch = isEnglishBeta
        && confirmEmail
        && normalizeEmail(email) !== normalizeEmail(confirmEmail);

    return (
        <div className="bg-slate-50 py-8 sm:py-20 min-h-screen">
            <div className="max-w-xl mx-auto px-3 sm:px-6">
                <div className="text-center mb-8 sm:mb-12">
                    <a href="/" className="inline-block mb-8">
                        <img src="/images/logo.png" alt="Sales Breaker Logo" className="h-16 w-16 mx-auto" onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/64x64/6366f1/ffffff?text=SB'; }}/>
                    </a>
                    <h1 className="text-2xl sm:text-4xl font-bold text-slate-900 tracking-tight">
                        {isEnglishBeta ? "Create your English Operator account" : "新規アカウント登録"}
                    </h1>
                    <p className="mt-4 text-slate-600">
                        {isEnglishBeta
                            ? "Operate governed outreach into Japanese companies from the SalesBreaker control plane."
                            : "Googleアカウントで即座に開始するか、メールアドレスで登録してください。"}
                    </p>
                    {isEnglishBeta && (
                        <p className="mt-3 text-sm text-slate-500">
                            Japanese company data, Japanese outreach copy, form conventions, and JST remain Japan-specific.
                        </p>
                    )}
                </div>

                <div className="bg-white p-5 sm:p-12 rounded-2xl shadow-xl space-y-8">
                    
                    {/* --- Google登録セクション --- */}
                    <div>
                        <button
                            type="button"
                            onClick={handleGoogleSignup}
                            className="w-full flex items-center justify-center gap-3 py-4 px-4 sm:px-8 border-2 border-slate-200 rounded-xl bg-white text-base sm:text-lg font-bold text-slate-700 hover:bg-slate-50 transition-all shadow-sm active:scale-[0.98]"
                        >
                            <img src="https://www.gstatic.com/images/branding/product/1x/gsa_512dp.png" className="w-6 h-6" alt="Google" />
                            {isEnglishBeta ? "Continue with Google" : "Googleでかんたん登録"}
                        </button>
                        <div className="mt-3 flex items-center justify-center gap-2">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                            <p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest">
                                {isEnglishBeta ? "Recommended for beta" : "入力不要"}
                            </p>
                        </div>
                    </div>

                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-slate-200"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-4 bg-white text-slate-400">
                                {isEnglishBeta ? "or sign up with email" : "またはメールアドレスで登録"}
                            </span>
                        </div>
                    </div>

                    {/* --- 通常フォームセクション --- */}
                    <form onSubmit={handleSubmit} method="post" className="space-y-6">
                        <div className="space-y-4">
                            <input type="text" name="company" autoComplete="organization" value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder={isEnglishBeta ? "Company name" : "会社名"} required className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 outline-none"/>
                            <input type="text" name="name" autoComplete="name" value={name} onChange={e => setName(e.target.value)} placeholder={isEnglishBeta ? "Operator name" : "ご担当者名"} required className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 outline-none"/>
                            <input type="tel" name="phone" autoComplete="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder={isEnglishBeta ? "Phone number" : "電話番号"} required className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 outline-none"/>
                            <div>
                                <input type="email" name="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={isEnglishBeta ? "Email address" : "メールアドレス"} required className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 outline-none"/>
                                {emailSuggestion && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setEmail(emailSuggestion);
                                            setConfirmEmail(emailSuggestion);
                                        }}
                                        className="mt-2 text-left text-xs font-semibold text-amber-700 hover:underline"
                                    >
                                        Did you mean {emailSuggestion}?
                                    </button>
                                )}
                            </div>
                            {isEnglishBeta && (
                                <div>
                                    <input
                                        type="email"
                                        name="confirm_email"
                                        autoComplete="email"
                                        value={confirmEmail}
                                        onChange={e => setConfirmEmail(e.target.value)}
                                        placeholder="Confirm email address"
                                        required
                                        className={`w-full px-4 py-3 bg-white border rounded-lg shadow-sm focus:ring-2 outline-none ${showConfirmEmailMismatch ? 'border-red-300 focus:ring-red-100' : 'border-slate-300 focus:ring-indigo-500'}`}
                                    />
                                    <p className={`mt-2 text-xs ${showConfirmEmailMismatch ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
                                        {showConfirmEmailMismatch
                                            ? "These email addresses do not match."
                                            : "We will send the verification link here. Check the spelling carefully."}
                                    </p>
                                </div>
                            )}
                            <input type="password" name="password" autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} placeholder={isEnglishBeta ? "Password (8+ characters)" : "パスワード (8文字以上)"} required className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 outline-none"/>

                            {showInviteCodeField && (
                                <div>
                                    <label htmlFor="inviteCode" className="text-sm font-medium text-slate-700 ml-1">{isEnglishBeta ? "Invitation code" : "招待コード"}</label>
                                    <input type="text" id="inviteCode" name="inviteCode" autoComplete="off" value={inviteCode} readOnly className="w-full px-4 py-3 bg-slate-100 border border-slate-300 rounded-lg"/>
                                </div>
                            )}
                        </div>

                        {/* Kept in the DOM for basic fill-all bots, but excluded from
                            rendering, focus, accessibility, and password managers. */}
                        <div
                            data-testid="signup-honeypot-wrapper"
                            hidden
                            aria-hidden="true"
                        >
                            <input
                                type="text"
                                name="website_hp"
                                data-testid="signup-honeypot"
                                tabIndex="-1"
                                autoComplete="off"
                                data-1p-ignore="true"
                                data-lpignore="true"
                                value={honeypot}
                                onChange={e => setHoneypot(e.target.value)}
                            />
                        </div>

                        {error && <div role="alert" className="text-red-600 text-sm p-3 bg-red-50 rounded-md border border-red-100">{error}</div>}
                        
                        <button disabled={processing} className="w-full bg-indigo-600 text-white font-bold py-4 px-4 sm:px-8 rounded-xl text-base sm:text-lg hover:bg-indigo-700 disabled:bg-indigo-400 transition-all shadow-lg active:scale-[0.98]">
                            {processing ? (isEnglishBeta ? 'Creating account...' : '登録処理中...') : (isEnglishBeta ? 'Agree and create account' : '同意して登録する')}
                        </button>

                         <p className="text-xs text-slate-500 text-center">
                            {isEnglishBeta ? (
                                <>
                                    By creating an account, you agree to the <a href={ENGLISH_BETA_TERMS_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-indigo-600">English Terms of Service</a>, <a href={ENGLISH_BETA_PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-indigo-600">English Privacy Policy</a>, and <a href={ENGLISH_BETA_SCTA_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-indigo-600">English commercial disclosure summary</a>. The Japanese documents remain authoritative. <a href={ENGLISH_BETA_LEGAL_SUMMARY_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-indigo-600">Legal overview</a>.
                                </>
                            ) : (
                                <>
                                    登録することで、<a href="/pages/terms.html" target="_blank" rel="noopener noreferrer" className="underline hover:text-indigo-600">利用規約</a>および<a href="/pages/privacy.html" target="_blank" rel="noopener noreferrer" className="underline hover:text-indigo-600">プライバシーポリシー</a>に同意したものとみなされます。
                                </>
                            )}
                        </p>
                    </form>
                </div>
                
                <div className="mt-8 text-center">
                    <Link to={withOperatorSurface("/login")} className="text-indigo-600 font-medium hover:underline">
                        {isEnglishBeta ? "Already have an account? Log in" : "既にアカウントをお持ちの方はこちら"}
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default SignupPage;
